'use client';
// AmanChain DEX — real on-chain AMM: swap, pools, liquidity, executed trades.
// Every displayed number comes from chain state: reserves, LP units, real swaps.
// The swap card is explicit about economics: effective price for YOUR size,
// price impact, minimum received, and pool depth — before you commit.
import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeftRight, Droplets, Flame, Plus, Minus, Loader2,
  TrendingUp, Activity, Info, Lock, AlertTriangle, ArrowDown, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { fetchJson, post, usePoll, fmtAmt, fmtInt, SectionHeader, StatCard, Mono, timeAgo, TokenAvatar } from './shared';
import { useSession, ConnectPrompt } from './session';
import { Sparkline } from './charts';
import type { Net, DexData, PoolViewT, SwapQuoteT, RecentSwapT } from './types';
import { ESSENTIALS, type EssentialAsset } from '@/lib/chain/essentials';

const netQ = (net: Net) => `net=${net}`;

interface MempoolEntryT { id: string; label: string; stuck: boolean; stuckReason: string | null; rejectedBlocks: number }

/** Warns when a submitted transaction is being refused by consensus — shows the exact chain reason. */
function MempoolAlerts({ net }: { net: Net }) {
  const { data } = usePoll<{ mempool: MempoolEntryT[] }>(
    async () => fetchJson(`/api/tx?${netQ(net)}`), 4000, [net]);
  const stuck = (data?.mempool ?? []).filter(t => t.stuck);
  if (stuck.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 space-y-1.5">
      {stuck.map(t => (
        <div key={t.id} className="flex items-start gap-2 text-xs text-amber-300">
          <Info className="size-3.5 mt-0.5 shrink-0" />
          <div>
            <span className="font-bold">{t.label}</span> is waiting but the chain refuses it ({t.rejectedBlocks} blocks):
            <span className="text-amber-200/90"> {t.stuckReason}</span>.
          </div>
        </div>
      ))}
    </div>
  );
}

export function DEX({ net }: { net: Net }) {
  const { session } = useSession();
  const address = session?.address ?? '';
  const [preferredPool, setPreferredPool] = useState('');
  const { data, refresh } = usePoll<DexData & { error?: string }>(
    async () => fetchJson(`/api/market?${netQ(net)}${address ? `&address=${encodeURIComponent(address)}` : ''}`),
    2500,
    [net, address],
  );

  if (!data) return <div className="flex items-center justify-center py-24 text-muted-foreground gap-2 text-sm"><Loader2 className="size-4 animate-spin" /> Loading the exchange…</div>;
  if (data.launched === false) {
    return <div className="py-24 text-center text-sm text-muted-foreground">Launch the network to open the DEX.</div>;
  }

  return (
    <div className="space-y-6">
      <SectionHeader
        icon={ArrowLeftRight}
        title="DEX — Swap & Liquidity"
        desc="On-chain automated market maker in consensus itself. The AMAN price is the real reserve ratio of the deepest pool — no oracles, no order books, no simulation."
      />

      <MempoolAlerts net={net} />

      {/* Market strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={TrendingUp} label="AMAN price (AMM spot)" accent="emerald"
          value={data.hasMarket ? `$${data.price.toFixed(4)}` : 'No pool yet'}
          sub={data.hasMarket ? `${data.change24h >= 0 ? '+' : ''}${(data.change24h * 100).toFixed(2)}% vs first swap in 24h` : 'Deploy a pool to set the first price'} />
        <StatCard icon={Droplets} label="Total liquidity (TVL)" accent="sky"
          value={<>{fmtAmt(data.tvlAman)} <span className="text-xs text-muted-foreground">AMAN</span></>}
          sub={`${data.pools.length} pool${data.pools.length === 1 ? '' : 's'} deployed`} />
        <StatCard icon={Activity} label="24h volume (AMAN legs)" accent="violet"
          value={<>{fmtAmt(data.volume24Aman)} <span className="text-xs text-muted-foreground">AMAN</span></>}
          sub="Executed swaps in the last 24 hours" />
        <StatCard icon={Flame} label="Burned by swaps" accent="rose"
          value={<>{fmtAmt(data.burnedBySwaps, 6)} <span className="text-xs text-muted-foreground">AMAN</span></>}
          sub="1/6 of every swap fee is destroyed forever" />
      </div>

      <EssentialsStrip data={data} onSelect={pid => setPreferredPool(pid)} />

      <div className="grid lg:grid-cols-2 gap-6">
        <SwapCard net={net} data={data} onDone={refresh} preferred={preferredPool} />
        {data.hasMarket && (
          <div className="glass p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-bold">Executed price line</div>
              <Mono>real swaps only</Mono>
            </div>
            {data.priceLine.length >= 2 ? (
              <Sparkline data={data.priceLine.map(p => p.p)} height={120} positive={data.change24h >= 0} />
            ) : (
              <div className="text-xs text-muted-foreground py-10 text-center">The chart draws itself from the second executed swap onward.</div>
            )}
            <div className="mt-3 space-y-1.5 max-h-44 overflow-y-auto">
              {data.recentSwaps.length === 0 && <div className="text-xs text-muted-foreground">No swaps executed yet.</div>}
              {data.recentSwaps.map(s => {
                const q = s.poolId.replace('AMAN-', '');
                const logo = data.pools.find(p => p.id === s.poolId)?.quoteLogo ?? data.tokens.find(t => t.symbol === q)?.logo;
                return <SwapRow key={s.txId} s={s} logoMap={new Map([[q, logo]])} />;
              })}
            </div>
          </div>
        )}
      </div>

      <PoolsSection net={net} data={data} onDone={refresh} />
    </div>
  );
}

// ---------- Network Essentials ----------
/** Canonical asset set of the network — dollar units + wrapped majors, all with live prices. */
function EssentialsStrip({ data, onSelect }: { data: DexData; onSelect: (poolId: string) => void }) {
  const rows = ESSENTIALS
    .map(asset => {
      const tok = data.tokens.find(t => t.symbol === asset.symbol);
      return tok ? { asset, tok, pool: data.pools.find(p => p.quote === asset.symbol) } : null;
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);
  if (rows.length === 0) return null;

  // USD reference: the deepest stable pool quotes AMAN directly in network dollars
  const stablePool = data.pools.filter(p => p.quoteStable).sort((a, b) => b.reserveAman - a.reserveAman)[0];
  const amanUsd = stablePool?.price ?? (data.hasMarket ? data.price : 0);
  const liveCount = rows.filter(({ asset }) => data.livePrices?.[asset.symbol]).length;

  return (
    <div className="space-y-2.5">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-sm font-bold">
          <Layers className="size-4 text-emerald-400" /> Network Essentials
        </span>
        <span className="text-[0.65rem] text-muted-foreground">
          The canonical asset set global platforms expect — deployed on-chain by the network issuer · click a card to trade it
        </span>
        {liveCount > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/30 px-2.5 py-0.5 text-[0.62rem] font-black">
            <span className={`size-1.5 rounded-full ${data.liveFeedOnline ? 'bg-emerald-400 pulse-dot' : 'bg-amber-400'}`} />
            LIVE MARKET PRICES · {data.liveSource ?? 'feed'} · {liveCount} assets
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {rows.map(({ asset, tok, pool }) => (
          <EssentialCard key={asset.symbol} asset={asset} tok={tok} pool={pool} amanUsd={amanUsd} live={data.livePrices?.[asset.symbol]} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
}

function EssentialCard({ asset, tok, pool, amanUsd, live, onSelect }: {
  asset: EssentialAsset;
  tok: { symbol: string; name: string; logo?: string; stable?: boolean; pegAman?: number };
  pool?: PoolViewT;
  amanUsd: number;
  live?: { usd: number; usd24hChange: number | null; source: string; stale: boolean };
  onSelect: (poolId: string) => void;
}) {
  const amanPerToken = pool && pool.price > 0 ? 1 / pool.price : 0;
  const ammUsd = amanPerToken > 0 ? amanPerToken * (amanUsd || 0) : 0;
  // Wrapped majors show the LIVE external market price; stables stay at their peg
  const showLive = live && live.usd > 0 && !tok.stable;
  const usd = showLive ? live!.usd : ammUsd;
  const chip = tok.stable
    ? { text: '100% AMAN-collateral', cls: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/30' }
    : asset.kind === 'wrapped'
      ? { text: 'Wrapped', cls: 'bg-sky-500/10 text-sky-400 ring-sky-500/30' }
      : { text: 'Network asset', cls: 'bg-violet-500/10 text-violet-400 ring-violet-500/30' };
  const chg = live?.usd24hChange ?? null;
  return (
    <button
      onClick={() => pool && onSelect(pool.id)}
      disabled={!pool}
      className={`glass p-3 text-left space-y-2 transition ${pool ? 'hover:ring-1 hover:ring-emerald-500/40 cursor-pointer' : 'opacity-70 cursor-default'}`}
      aria-label={`Trade ${asset.symbol}`}
    >
      <div className="flex items-center gap-2">
        <TokenAvatar symbol={asset.symbol} logo={tok.logo} size={30} />
        <div className="min-w-0 flex-1">
          <div className="font-black text-sm leading-none" style={{ color: asset.color }}>{asset.symbol}</div>
          <div className="text-[0.6rem] text-muted-foreground truncate">{asset.name}</div>
        </div>
        {showLive && (
          <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[0.52rem] font-black ${live!.stale ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
            <span className={`size-1 rounded-full ${live!.stale ? 'bg-amber-400' : 'bg-emerald-400 pulse-dot'}`} /> LIVE
          </span>
        )}
      </div>
      <div>
        {pool ? (
          <>
            <div className="text-base font-black" dir="ltr">{usd > 0 ? `$${fmtPrice(usd)}` : '—'}</div>
            <div className="flex items-center gap-1.5">
              <Mono className="text-[0.6rem]">{fmtPrice(amanPerToken)} AMAN</Mono>
              {showLive && chg !== null && (
                <span className={`text-[0.58rem] font-black ${chg >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} dir="ltr">
                  {chg >= 0 ? '▲' : '▼'} {Math.abs(chg).toFixed(2)}%
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="text-xs text-muted-foreground font-bold">Pool pending</div>
        )}
      </div>
      <div className="flex items-center justify-between gap-1">
        <span className={`rounded-full px-1.5 py-0.5 text-[0.55rem] font-bold ring-1 ${chip.cls}`}>{chip.text}</span>
        {pool && <Mono className="text-[0.55rem]">TVL {fmtAmt(pool.tvlAman, 0)}</Mono>}
      </div>
    </button>
  );
}

function fmtPrice(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return '0';
  if (v >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (v >= 1) return v.toFixed(2);
  if (v >= 0.01) return v.toFixed(4);
  return v.toFixed(8).replace(/0+$/, '');
}

function SwapRow({ s, logoMap }: { s: RecentSwapT; logoMap?: Map<string, string | undefined> }) {
  const toToken = s.direction === 'aman_to_token';
  const qSym = s.poolId.replace('AMAN-', '');
  return (
    <div className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-xs">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`rounded-full px-2 py-0.5 font-bold ${toToken ? 'bg-emerald-500/10 text-emerald-400' : 'bg-sky-500/10 text-sky-400'}`}>
          {toToken ? 'BUY' : 'SELL'}
        </span>
        <TokenAvatar symbol={qSym} logo={logoMap?.get(qSym)} size={18} />
        <Mono className="truncate">{fmtAmt(s.amountIn)} {toToken ? 'AMAN' : qSym} → {fmtAmt(s.amountOut)} {toToken ? qSym : 'AMAN'}</Mono>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <Mono>@ ${s.priceOfAman.toFixed(4)}</Mono>
        <span className="text-muted-foreground">{timeAgo(s.ts)}</span>
      </div>
    </div>
  );
}

// ---------- Swap card ----------
function PairAvatars({ pool, size = 26 }: { pool: { quote: string; quoteLogo?: string }; size?: number }) {
  return (
    <span className="inline-flex items-center shrink-0">
      <TokenAvatar symbol="AMAN" size={size} className="z-10" />
      <TokenAvatar symbol={pool.quote} logo={pool.quoteLogo} size={size} className="-ml-2" />
    </span>
  );
}

function impactColor(p: number): string {
  if (p < 0.01) return 'text-emerald-400';
  if (p < 0.05) return 'text-amber-400';
  if (p < 0.20) return 'text-orange-400';
  return 'text-rose-400';
}

function SwapCard({ net, data, onDone, preferred }: {
  net: Net; data: DexData; onDone: () => void; preferred?: string;
}) {
  const { session, wallet, openAccount } = useSession();
  const [poolId, setPoolId] = useState('');
  const [side, setSide] = useState<'buy' | 'sell'>('buy'); // buy = pay AMAN → get token; sell = pay token → get AMAN
  const [amount, setAmount] = useState('');
  const [slippage, setSlippage] = useState('1.5');
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState<SwapQuoteT | null>(null);

  // Essentials strip (or any deep-link) can drive the selected pool
  useEffect(() => {
    if (preferred) { setPoolId(preferred); setQuote(null); }
  }, [preferred]);

  const pool = data.pools.find(p => p.id === poolId) ?? data.pools[0];
  const activePoolId = pool?.id ?? '';
  const quoteSym = pool?.quote ?? '';
  const direction = side === 'buy' ? 'aman_to_token' as const : 'token_to_aman' as const;
  const tokenBalance = wallet?.assets.find(a => a.symbol === quoteSym)?.balance ?? 0;
  const amanBalance = wallet?.balance ?? 0;
  const amt = Number(amount) || 0;
  const maxIn = side === 'buy' ? amanBalance : tokenBalance;

  const doQuote = useCallback(async () => {
    if (!activePoolId || amt <= 0) { setQuote(null); return; }
    try {
      const r = await post<{ quote: SwapQuoteT }>(`/api/market?${netQ(net)}`, {
        poolId: activePoolId, direction, amountIn: amt,
      });
      setQuote(r.quote);
    } catch { setQuote(null); }
  }, [net, activePoolId, direction, amt]);

  // Debounced quote
  useEffect(() => {
    const t = setTimeout(doQuote, 250);
    return () => clearTimeout(t);
  }, [doQuote]);

  const switchSide = (s: 'buy' | 'sell') => { setSide(s); setAmount(''); setQuote(null); };

  const execute = async () => {
    if (!session) { openAccount(); return; }
    if (!quote || amt <= 0) { toast.error('Enter an amount to swap'); return; }
    if (amt > maxIn) { toast.error(`Insufficient ${side === 'buy' ? 'AMAN' : quoteSym} balance`); return; }
    const slipPct = Math.max(0.1, Math.min(50, Number(slippage) || 1.5));
    const minOut = Math.max(0, quote.out * (1 - slipPct / 100));
    setBusy(true);
    try {
      await post(`/api/tx?${netQ(net)}`, {
        walletId: session.walletId, password: session.password, type: 'swap',
        poolId: activePoolId, direction, amount: amt,
        minOut, deadline: Date.now() + 5 * 60_000,
      });
      toast.success(`Swap submitted — ${fmtAmt(amt)} ${side === 'buy' ? 'AMAN' : quoteSym} → ~${fmtAmt(quote.out)} ${side === 'buy' ? quoteSym : 'AMAN'}`);
      setAmount(''); setQuote(null);
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Swap failed');
    } finally {
      setBusy(false);
    }
  };

  const impact = quote?.priceImpact ?? 0;
  const extreme = impact >= 0.20;

  return (
    <div className="glass p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold">Swap</div>
        <span className="text-[0.65rem] text-muted-foreground">Fee 0.30% · 1/6 burned · 5/6 to liquidity providers</span>
      </div>

      {data.pools.length === 0 ? (
        <div className="text-xs text-muted-foreground flex items-start gap-2 p-3 rounded-lg bg-amber-500/5 ring-1 ring-amber-500/20">
          <Info className="size-4 text-amber-400 shrink-0" />
          No pools exist yet. Create the first pool below — the pair ratio you deposit becomes the initial AMAN market price.
        </div>
      ) : (
        <>
          {/* Pair selector */}
          <div className="flex items-center gap-2">
            <span className="shrink-0"><PairAvatars pool={pool ?? { quote: '' }} /></span>
            <select value={activePoolId} onChange={e => { setPoolId(e.target.value); setQuote(null); }}
              className="flex-1 rounded-lg bg-secondary ring-1 ring-border px-3 py-2 text-sm font-bold" aria-label="Pool">
              {data.pools.map(p => (
                <option key={p.id} value={p.id}>AMAN / {p.quote} — ${p.price.toFixed(4)}{p.quoteStable ? ' · stable peg' : ''} {p.frozen ? '(frozen)' : ''}</option>
              ))}
            </select>
          </div>

          {/* BUY / SELL tabs — explicit direction for every token */}
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-muted/60 ring-1 ring-border p-1">
            <button onClick={() => switchSide('buy')} aria-pressed={side === 'buy'}
              className={`rounded-lg py-2 text-xs font-black transition inline-flex items-center justify-center gap-1.5 ${side === 'buy' ? 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/40' : 'text-muted-foreground hover:text-foreground'}`}>
              <Plus className="size-3.5" /> BUY {quoteSym}
            </button>
            <button onClick={() => switchSide('sell')} aria-pressed={side === 'sell'}
              className={`rounded-lg py-2 text-xs font-black transition inline-flex items-center justify-center gap-1.5 ${side === 'sell' ? 'bg-sky-500/15 text-sky-400 ring-1 ring-sky-500/40' : 'text-muted-foreground hover:text-foreground'}`}>
              <Minus className="size-3.5" /> SELL {quoteSym}
            </button>
          </div>

          {/* Amount in */}
          <div className="rounded-xl bg-muted/40 ring-1 ring-border p-3 space-y-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{side === 'buy' ? 'You pay (AMAN)' : `You pay (${quoteSym})`}</span>
              {session && maxIn > 0 && (
                <button className="text-primary font-bold" onClick={() => setAmount(String(Math.max(0, Math.floor(maxIn * 1e4) / 1e4)))}>MAX</button>
              )}
            </div>
            <input value={amount} onChange={e => { setAmount(e.target.value.replace(/[^0-9.]/g, '')); }}
              inputMode="decimal" dir="ltr" placeholder="0.0" aria-label="Amount in"
              className="w-full bg-transparent text-xl font-black outline-none" />
            <Mono>{session
              ? `Balance: ${fmtAmt(side === 'buy' ? amanBalance : tokenBalance)} ${side === 'buy' ? 'AMAN' : quoteSym}`
              : 'Connect your wallet to see your balance'}</Mono>
          </div>

          <div className="flex justify-center -my-2"><ArrowDown className="size-4 text-muted-foreground" /></div>

          {/* Amount out */}
          <div className="rounded-xl bg-muted/40 ring-1 ring-border p-3 space-y-1.5">
            <div className="text-xs text-muted-foreground">You receive (est.) — {side === 'buy' ? quoteSym : 'AMAN'}</div>
            <div className="text-xl font-black" dir="ltr">{quote ? fmtAmt(quote.out) : '—'}</div>
            {quote && (
              <div className="space-y-1 pt-1 border-t border-border/60 mt-1.5">
                <div className="flex items-center justify-between text-[0.68rem]">
                  <span className="text-muted-foreground">Effective price (for this size)</span>
                  <Mono>${(direction === 'aman_to_token' ? amt / Math.max(1e-9, quote.out) : quote.out / Math.max(1e-9, amt)).toFixed(6)} per AMAN</Mono>
                </div>
                <div className="flex items-center justify-between text-[0.68rem]">
                  <span className="text-muted-foreground">Price impact</span>
                  <span className={`font-black ${impactColor(impact)}`} dir="ltr">{(impact * 100).toFixed(2)}%</span>
                </div>
                <div className="flex items-center justify-between text-[0.68rem]">
                  <span className="text-muted-foreground">Minimum received (slippage guard)</span>
                  <Mono>{fmtAmt(quote.out * (1 - Math.max(0.1, Math.min(50, Number(slippage) || 1.5)) / 100))} {side === 'buy' ? quoteSym : 'AMAN'}</Mono>
                </div>
                <div className="flex items-center justify-between text-[0.68rem]">
                  <span className="text-muted-foreground">LP fee (1/6 burned)</span>
                  <Mono>{fmtAmt(quote.feeTotal, 6)} {side === 'buy' ? 'AMAN' : quoteSym}</Mono>
                </div>
                <div className="flex items-center justify-between text-[0.68rem]">
                  <span className="text-muted-foreground">Pool depth (TVL)</span>
                  <Mono>{fmtAmt(pool?.tvlAman)} AMAN</Mono>
                </div>
                <div className="flex items-center justify-between text-[0.68rem]">
                  <span className="text-muted-foreground">Price after your trade</span>
                  <Mono>${quote.priceAfter.toFixed(6)}</Mono>
                </div>
              </div>
            )}
          </div>

          {/* Extreme impact warning — AMM honesty */}
          {quote && extreme && (
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2.5 flex items-start gap-2 text-xs text-rose-300">
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              <div>
                <b>This pool is too small for this size.</b> {(impact * 100).toFixed(1)}% of your trade is lost to price impact —
                you are trading {(amt / Math.max(1e-9, pool?.reserveAman ?? 1)).toFixed(1)}× the AMAN reserve.
                Reduce the amount or add liquidity to the pool first; the effective price above is the real AMM math, not an error.
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">Slippage tolerance %</span>
              <input value={slippage} onChange={e => setSlippage(e.target.value.replace(/[^0-9.]/g, ''))}
                inputMode="decimal" dir="ltr" className="w-full rounded-lg bg-secondary ring-1 ring-border px-3 py-2 text-sm" />
            </label>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Wallet</span>
              <div className="rounded-lg bg-muted/40 ring-1 ring-border px-3 py-2 text-sm font-bold truncate">
                {session ? session.name : <span className="text-muted-foreground font-normal">Not connected</span>}
              </div>
            </div>
          </div>

          <button onClick={execute} disabled={busy || !quote || !session || (!session && true)}
            className="w-full rounded-xl bg-primary text-primary-foreground font-black py-3 disabled:opacity-40 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="size-4 animate-spin" /> : !session ? <Lock className="size-4" /> : <ArrowLeftRight className="size-4" />}
            {!session ? 'Connect wallet to swap' : busy ? 'Signing with both keys…' : `${side === 'buy' ? 'Buy' : 'Sell'} ${quoteSym}`}
          </button>
          <p className="text-[0.65rem] text-muted-foreground">
            The swap is a signed on-chain transaction (Ed25519 + ML-DSA-87). Slippage protection reverts it if the price moves beyond your tolerance before mining.
          </p>
        </>
      )}
    </div>
  );
}

// ---------- Pools + liquidity ----------
function PoolsSection({ net, data, onDone }: {
  net: Net; data: DexData; onDone: () => void;
}) {
  return (
    <div className="space-y-4">
      <SectionHeader icon={Droplets} title="Liquidity pools"
        desc="LP units are consensus objects — reserves sit in the chain state and earn 5/6 of every swap fee." />
      <CreatePoolCard net={net} data={data} onDone={onDone} />
      <div className="grid md:grid-cols-2 gap-4">
        {data.pools.length === 0 && (
          <div className="text-xs text-muted-foreground md:col-span-2">No pools yet — deploy the first pair above.</div>
        )}
        {data.pools.map(p => (
          <PoolCard key={p.id} net={net} pool={p} onDone={onDone} />
        ))}
      </div>
    </div>
  );
}

function CreatePoolCard({ net, data, onDone }: {
  net: Net; data: DexData; onDone: () => void;
}) {
  const { session, wallet, openAccount } = useSession();
  const openTokens = data.tokens.filter(t => !t.hasPool);
  const [sym, setSym] = useState('');
  const [amanAmt, setAmanAmt] = useState('');
  const [tokAmt, setTokAmt] = useState('');
  const [busy, setBusy] = useState(false);
  const tokBalance = wallet?.assets.find(a => a.symbol === sym)?.balance ?? 0;

  const deploy = async () => {
    if (!session) { openAccount(); return; }
    if (!sym || !(Number(amanAmt) > 0) || !(Number(tokAmt) > 0)) { toast.error('Choose a token and fill both sides'); return; }
    setBusy(true);
    try {
      await post(`/api/tx?${netQ(net)}`, {
        walletId: session.walletId, password: session.password, type: 'pool_create',
        quote: sym, amount: Number(amanAmt), quoteAmount: Number(tokAmt),
      });
      toast.success(`Pool AMAN/${sym} deployed — initial price $${(Number(tokAmt) / Number(amanAmt)).toFixed(4)}`);
      setAmanAmt(''); setTokAmt('');
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Pool deployment failed');
    } finally {
      setBusy(false);
    }
  };

  if (openTokens.length === 0) {
    return (
      <div className="glass p-4 text-xs text-muted-foreground flex items-center gap-2">
        <Lock className="size-4 text-emerald-400" />
        Every deployed token already has a pool. Deploy a new token in the Token Lab to open another market.
      </div>
    );
  }

  return (
    <div className="glass p-4 space-y-3">
      <div className="text-sm font-bold">Deploy a new pool</div>
      <div className="grid md:grid-cols-2 gap-3">
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Token without a pool</span>
          <select value={sym} onChange={e => setSym(e.target.value)}
            className="w-full rounded-lg bg-secondary ring-1 ring-border px-3 py-2 text-sm" aria-label="Token">
            <option value="">Select…</option>
            {openTokens.map(t => <option key={t.symbol} value={t.symbol}>{t.symbol} — {t.name}{t.frozen ? ' (frozen)' : ''}</option>)}
          </select>
        </label>
        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">Your wallet</span>
          <div className="rounded-lg bg-muted/40 ring-1 ring-border px-3 py-2 text-sm font-bold truncate">
            {session ? `${session.name} · ${fmtAmt(wallet?.balance ?? 0)} AMAN` : 'Not connected'}
          </div>
        </div>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Initial AMAN liquidity (min 10)</span>
          <input value={amanAmt} onChange={e => setAmanAmt(e.target.value.replace(/[^0-9.]/g, ''))}
            inputMode="decimal" dir="ltr" placeholder="1000" className="w-full rounded-lg bg-secondary ring-1 ring-border px-3 py-2 text-sm" />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Initial {sym || 'token'} liquidity (min 10) {session && sym ? `· you hold ${fmtAmt(tokBalance)}` : ''}</span>
          <input value={tokAmt} onChange={e => setTokAmt(e.target.value.replace(/[^0-9.]/g, ''))}
            inputMode="decimal" dir="ltr" placeholder="1000" className="w-full rounded-lg bg-secondary ring-1 ring-border px-3 py-2 text-sm" />
        </label>
      </div>
      {amanAmt && tokAmt && Number(amanAmt) > 0 && (
        <Mono>Initial price: ${(Number(tokAmt) / Number(amanAmt)).toFixed(4)} per AMAN · initial LP: {fmtAmt(Math.sqrt(Number(amanAmt) * Number(tokAmt)))}</Mono>
      )}
      <button onClick={deploy} disabled={busy}
        className="rounded-xl bg-primary text-primary-foreground font-black px-6 py-2.5 disabled:opacity-40 inline-flex items-center gap-2">
        {busy ? <Loader2 className="size-4 animate-spin" /> : !session ? <Lock className="size-4" /> : <Plus className="size-4" />}
        {session ? 'Deploy pool' : 'Connect wallet to deploy'}
      </button>
    </div>
  );
}

function PoolCard({ net, pool, onDone }: {
  net: Net; pool: PoolViewT; onDone: () => void;
}) {
  const [mode, setMode] = useState<'none' | 'add' | 'remove'>('none');
  return (
    <div className="glass p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2.5">
          <PairAvatars pool={pool} size={30} />
          <div>
            <div className="font-black">AMAN / {pool.quote}</div>
            <Mono>{pool.quoteName}{pool.frozen ? ' · frozen by governance' : ''}</Mono>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-black text-emerald-400" dir="ltr">${pool.price.toFixed(4)}</div>
          <Mono>per AMAN</Mono>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Kv k="AMAN reserve" v={fmtAmt(pool.reserveAman)} />
        <Kv k={`${pool.quote} reserve`} v={fmtAmt(pool.reserveToken)} />
        <Kv k="TVL (AMAN)" v={fmtAmt(pool.tvlAman)} />
        <Kv k="24h volume" v={fmtAmt(pool.vol24Aman)} />
        <Kv k="Lifetime swaps" v={fmtInt(pool.swaps)} />
        <Kv k="Fees earned (AMAN eq.)" v={fmtAmt(pool.feesAman, 6)} />
        <Kv k="Burned AMAN" v={fmtAmt(pool.burnedAman, 6)} />
        <Kv k="Burned {q}" v={fmtAmt(pool.burnedQuote, 6)} k2={pool.quote} />
      </div>
      {pool.yourLp ? (
        <div className="rounded-lg bg-emerald-500/5 ring-1 ring-emerald-500/20 p-2.5 text-xs space-y-0.5">
          <div className="font-bold text-emerald-400">Your position — {((pool.yourLp / Math.max(1e-9, pool.lpSupply)) * 100).toFixed(2)}% of the pool</div>
          <Mono>LP {fmtAmt(pool.yourLp, 6)} ≈ {fmtAmt(pool.yourAman)} AMAN + {fmtAmt(pool.yourToken)} {pool.quote}</Mono>
        </div>
      ) : null}
      <div className="flex gap-2">
        <button onClick={() => setMode(m => (m === 'add' ? 'none' : 'add'))}
          className="flex-1 rounded-lg ring-1 ring-border py-2 text-xs font-bold hover:ring-primary/40 transition inline-flex items-center justify-center gap-1.5">
          <Plus className="size-3.5" /> Add liquidity
        </button>
        {pool.yourLp ? (
          <button onClick={() => setMode(m => (m === 'remove' ? 'none' : 'remove'))}
            className="flex-1 rounded-lg ring-1 ring-border py-2 text-xs font-bold hover:ring-primary/40 transition inline-flex items-center justify-center gap-1.5">
            <Minus className="size-3.5" /> Remove
          </button>
        ) : null}
      </div>
      {mode === 'add' && <AddLiquidity net={net} pool={pool} onDone={onDone} close={() => setMode('none')} />}
      {mode === 'remove' && <RemoveLiquidity net={net} pool={pool} onDone={onDone} close={() => setMode('none')} />}
    </div>
  );
}

function Kv({ k, v, k2 }: { k: string; v: string; k2?: string }) {
  return (
    <div className="flex items-center justify-between bg-muted/40 rounded-lg px-2.5 py-1.5">
      <span className="text-muted-foreground">{k2 ? k.replace('{q}', k2) : k}</span>
      <span className="font-bold" dir="ltr">{v}</span>
    </div>
  );
}

function AddLiquidity({ net, pool, onDone, close }: {
  net: Net; pool: PoolViewT; onDone: () => void; close: () => void;
}) {
  const { session, wallet, openAccount } = useSession();
  const [amanAmt, setAmanAmt] = useState('');
  const [busy, setBusy] = useState(false);
  const aman = Number(amanAmt) || 0;
  const needTok = aman > 0 ? (aman * pool.reserveToken) / pool.reserveAman : 0;
  const tokBal = wallet?.assets.find(a => a.symbol === pool.quote)?.balance ?? 0;

  const add = async () => {
    if (!session) { openAccount(); return; }
    if (!(aman > 0)) { toast.error('Fill the deposit amount'); return; }
    setBusy(true);
    try {
      await post(`/api/tx?${netQ(net)}`, {
        walletId: session.walletId, password: session.password, type: 'pool_add',
        poolId: pool.id, quote: pool.quote,
        amount: aman, quoteAmount: needTok,
      });
      toast.success(`Added ${fmtAmt(aman)} AMAN + ${fmtAmt(needTok)} ${pool.quote} to ${pool.id}`);
      setAmanAmt(''); close(); onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Add liquidity failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 rounded-xl ring-1 ring-border p-3 bg-muted/20">
      <label className="space-y-1 block">
        <span className="text-xs text-muted-foreground">AMAN deposit — pool ratio requires exactly {(needTok).toFixed(6)} {pool.quote} alongside</span>
        <input value={amanAmt} onChange={e => setAmanAmt(e.target.value.replace(/[^0-9.]/g, ''))}
          inputMode="decimal" dir="ltr" placeholder="0.0" className="w-full rounded-lg bg-secondary ring-1 ring-border px-3 py-2 text-sm" />
      </label>
      {aman > 0 && <Mono>You need {fmtAmt(needTok, 6)} {pool.quote} {session ? `· you hold ${fmtAmt(tokBal)}` : ''} · LP ≈ {fmtAmt((aman * pool.lpSupply) / pool.reserveAman, 6)}</Mono>}
      <button onClick={add} disabled={busy}
        className="w-full rounded-xl bg-primary text-primary-foreground font-black py-2.5 disabled:opacity-40 inline-flex items-center justify-center gap-2">
        {busy ? <Loader2 className="size-4 animate-spin" /> : !session ? <Lock className="size-4" /> : <Plus className="size-4" />}
        {session ? 'Add liquidity' : 'Connect wallet first'}
      </button>
    </div>
  );
}

function RemoveLiquidity({ net, pool, onDone, close }: {
  net: Net; pool: PoolViewT; onDone: () => void; close: () => void;
}) {
  const { session } = useSession();
  const [pct, setPct] = useState('50');
  const [busy, setBusy] = useState(false);
  const lp = ((Number(pct) || 0) / 100) * (pool.yourLp ?? 0);
  const amanOut = pool.lpSupply > 0 ? (lp * pool.reserveAman) / pool.lpSupply : 0;
  const tokOut = pool.lpSupply > 0 ? (lp * pool.reserveToken) / pool.lpSupply : 0;

  const remove = async () => {
    if (!session) { toast.error('Connect your wallet first'); return; }
    if (!(lp > 0)) { toast.error('Enter a percentage'); return; }
    setBusy(true);
    try {
      await post(`/api/tx?${netQ(net)}`, {
        walletId: session.walletId, password: session.password, type: 'pool_remove',
        poolId: pool.id, lpAmount: lp,
      });
      toast.success(`Removed ${fmtAmt(amanOut)} AMAN + ${fmtAmt(tokOut)} ${pool.quote}`);
      close(); onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 rounded-xl ring-1 ring-border p-3 bg-muted/20">
      <label className="space-y-1 block">
        <span className="text-xs text-muted-foreground">Share of your LP to burn (%) — you hold {fmtAmt(pool.yourLp ?? 0, 6)} LP</span>
        <input value={pct} onChange={e => setPct(e.target.value.replace(/[^0-9.]/g, ''))}
          inputMode="decimal" dir="ltr" className="w-full rounded-lg bg-secondary ring-1 ring-border px-3 py-2 text-sm" />
      </label>
      {lp > 0 && <Mono>≈ {fmtAmt(amanOut)} AMAN + {fmtAmt(tokOut)} {pool.quote}</Mono>}
      <button onClick={remove} disabled={busy}
        className="w-full rounded-xl bg-rose-500 text-white font-black py-2.5 disabled:opacity-40 inline-flex items-center justify-center gap-2">
        {busy ? <Loader2 className="size-4 animate-spin" /> : <Minus className="size-4" />} Remove liquidity
      </button>
    </div>
  );
}
